/**
 * Générateur — Automatisme : lire l'abscisse d'un point sur une droite
 * graduée en tiers, en quarts, en moitiés, en dixièmes.
 * Attendu ID : 1050 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025). Le geste, construit au CM1 (la
 * fraction-mesure) : je compte les parts dans une unité, ça donne le bas ;
 * je compte les parts depuis 0, ça donne le haut. Second chemin : l'entier
 * passé, plus les parts qui restent (7/4 = 1 + 3/4). Les tiers interdisent
 * le décimal, ils obligent à écrire une fraction.
 *
 * Plan de cartes validé (cadrage prof 2026-09-07), trois cartes :
 *   - Série flash             (4 droites A, B, C, D, une par dénominateur,
 *                              saisie fraction deux cases)
 *   - Quelle graduation ?     (QCM, Saisie : « l'unité est partagée en …
 *                              parts égales », le préalable)
 *   - Lire l'abscisse         (QCM, Relier 4 points + 2 leurres, Vrai/Faux
 *                              3 affirmations aux formes mêlées)
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Demi-droite positive (les relatifs sont l'objectif de l'année), unité
 * partagée en 2, 3, 4 ou 10 parts, sous-graduations visibles, abscisse
 * jusqu'à 3, JAMAIS sur un entier. Le point est toujours sur une graduation
 * (rang entier i, valeur i/den ; l'app place les traits par comptage, les
 * tiers tombent juste). Série flash : points premiers avec le dénominateur
 * et valeurs toutes distinctes dans une série (les fractions égales sont
 * l'automatisme 1051). Cartes 2 et 3 : simplifiable (2/4, 5/10) autorisé une
 * fois sur deux, la réponse reste la fraction de la graduation.
 * Figure : élément natif « numberline » (écran et PDF). La valeur cherchée
 * n'est jamais étiquetée (jamais dans `reperes`, jamais `showValue`).
 * Étiquettes d'entiers : tous, ou 0 et 1, ou 0 et 2 (la valeur d'une part
 * se déduit). Origine au bord (demi-droite) ou trait prolongé à gauche
 * (« droite », lineType full) une fois sur trois.
 * POINT SACRIFIÉ : 5/3 (le coup de pouce, droite de 0 à 2 en tiers).
 *
 * ── Écritures ────────────────────────────────────────────────────────────
 * Saisie (Série flash) : fraction de la graduation SEULE, annoncée dans la
 * consigne (« le dénominateur est le nombre de parts d'une unité ») : la
 * correction des trous est positionnelle, 2/4 attendu et non 1/2, ce qui
 * montre que l'élève a lu la graduation. Les trois formes (7/4, 1 + 3/4,
 * 1,75) vivent au QCM (clé décimale une fois sur trois quand elle existe)
 * et au Vrai/Faux.
 *
 * ── Distracteurs (familles nommées, cohérentes entre les cartes) ─────────
 *   traits_interieurs : i/(den − 1), il compte les traits entre 0 et 1
 *                       (3 traits en quarts) — L'ERREUR PRINCIPALE ;
 *                       carte 2 : den − 1 (sauf moitiés : 1 part ne trompe
 *                       personne, remplacé par le total des graduations) ;
 *   traits_bornes     : (i + 1)/(den + 1), il compte tous les traits, bornes
 *                       comprises, depuis 0 — en rotation ; carte 2 : den + 1 ;
 *   toutes_graduations: carte 2, le nombre de traits de toute la droite ;
 *   decalage          : (i ± 1)/den ;
 *   oubli_entier      : (i mod den)/den, il repart de 0 après l'entier ;
 *   coller            : entier et numérateur collés, 13/4 ou 1,3 pour
 *                       1 + 3/4 (1,3 est FAUX en moitiés, tiers, quarts ;
 *                       juste en dixièmes, donc exclu là) ;
 *   inversion         : den/i.
 * Chaque distracteur est vérifié ≠ clé EN VALEUR (produit en croix) et les
 * distracteurs entre eux ; collision → nouveau tirage.
 */
import {
    randomChoice, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, tex,
} from '../../../utils';

const ATTENDU_ID = 1050;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_GRAD = 'Quelle graduation ?';
const L_LIRE = 'Lire l\'abscisse';

// ─── Le domaine ───────────────────────────────────────────────────────────

const DENS = [2, 3, 4, 10];
const END_MAX = 3;
const COULEUR_POINT = '#fb923c';

/** Le point de l'exemple du coup de pouce, sacrifié : 5/3. */
export const POINT_SACRIFIE = { num: 5, den: 3 };
const estSacrifie = (i, den) => den === POINT_SACRIFIE.den && i === POINT_SACRIFIE.num;

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

/**
 * Fin maximale de la droite : 3 partout, sauf les TIERS dans le QCM « Lire
 * l'abscisse » et le Relier, prolongés jusqu'à 4 (revue prof 2026-09-08) :
 * avec 5/3 sacrifié, une droite en tiers arrêtée à 3 n'a que cinq positions
 * (trop peu pour quatre points et deux leurres) et le distracteur « traits »
 * de 7/3 ou 8/3 (7/2, 8/2) tombait hors de la droite.
 */
const finMaxLire = (den) => (den === 3 ? 4 : END_MAX);

/** Tous les rangs valides pour un dénominateur : 1 ≤ i < finMax·den, non entier, non sacrifié. */
const rangsValides = (den, { auDela = null, simplifiable = null, exclus = [], finMax = END_MAX } = {}) => {
    const tous = [];
    for (let i = 1; i < finMax * den; i++) {
        if (i % den === 0 || estSacrifie(i, den) || exclus.includes(i)) continue;
        if (auDela === true && i < den) continue;
        if (auDela === false && i > den) continue;
        tous.push(i);
    }
    if (simplifiable === null) return tous;
    const filtres = tous.filter((i) => (gcd(i, den) > 1) === simplifiable);
    return filtres.length ? filtres : tous;
};

/** Un rang tiré au hasard parmi les valides (repli : le premier). */
const tirerRang = (den, contraintes = {}) => {
    const valides = rangsValides(den, contraintes);
    if (!valides.length) throw new Error(`gen_1050 : aucun rang valide pour den = ${den}`);
    return randomChoice(valides);
};

/** Fin de la droite : l'entier suivant le plus grand point, plafonné (3, ou 4 en tiers pour QCM et Relier). */
const finDroite = (rangs, den, finMax = END_MAX) => Math.min(finMax, Math.floor(Math.max(...rangs) / den) + 2);

const ETIQUETTES = ['tous', 'zero_un', 'zero_deux'];

/**
 * Configuration « numberline » : pas 1/den, points nommés, étiquettes
 * d'entiers selon le mode. La valeur d'un point n'est JAMAIS étiquetée.
 */
const configDroite = (den, points, { end, etiquettes = 'tous', lineType = 'half' }) => {
    let reperes;
    let showEnd = true;
    if (etiquettes === 'tous') reperes = Array.from({ length: end - 1 }, (_, k) => k + 1);
    else if (etiquettes === 'zero_un') { reperes = [1]; showEnd = false; }
    else { reperes = end === 2 ? [] : [2]; showEnd = end === 2; }
    return {
        start: 0, end, step: 1 / den, reperes,
        points: points.map((p) => ({ value: p.i / den, label: p.label, color: COULEUR_POINT })),
        lineType, showArrow: true, showStart: true, showEnd,
    };
};
const elDroite = (config) => ({ type: 'numberline', config });
const elements = (list) => ({ version: 1, elements: list });
const support = (lineType) => (lineType === 'half' ? 'demi-droite' : 'droite');
const tirerLineType = () => randomChoice(['half', 'half', 'full']);

// ─── Écritures ────────────────────────────────────────────────────────────

const fracTex = (n, d) => `\\dfrac{${n}}{${d}}`;
/** Fraction à deux cases (gabarit 622), numérateur puis dénominateur. */
const fracDeuxCases = (n, d) => `\\dfrac{{{${n}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${d}}}}`;
const aDecimal = (den) => den !== 3;
/** i/den en écriture décimale française (den divise 100), exact par les centièmes. */
const decStr = (i, den) => {
    const cent = (i * 100) / den;
    const q = Math.floor(cent / 100);
    const r = cent % 100;
    if (r === 0) return String(q);
    return `${q},${String(r).padStart(2, '0').replace(/0$/, '')}`;
};
const mixteTex = (i, den) => `${Math.floor(i / den)} + ${fracTex(i % den, den)}`;
/** Égalité rationnelle en croix. */
const memeValeur = (p, q, r, s) => p * s === r * q;

// ─── Distracteurs ─────────────────────────────────────────────────────────

const EXPLICATIONS = {
    traits_interieurs: 'Je compte les parts entre 0 et 1, pas les traits : en quarts, il y a 3 traits entre 0 et 1 mais 4 parts.',
    traits_bornes: 'Je compte les parts, pas les traits : de 0 à 1 en quarts, il y a 5 traits mais 4 parts.',
    toutes_graduations: 'Je compte les parts d\'une seule unité, entre 0 et 1, pas toutes les graduations de la droite.',
    decalage: 'Je repars du dernier entier et je compte les parts une à une, sans en sauter ni en ajouter.',
    oubli_entier: 'Après 1, les parts continuent : 1 et 3 quarts, c\'est 7 quarts, pas 3 quarts.',
    coller: '1 et 3 quarts ne s\'écrit pas 1,3 : 1,3 c\'est 1 et 3 dixièmes.',
    inversion: 'Le bas, c\'est le nombre de parts dans une unité ; le haut, le nombre de parts depuis 0.',
    decalage_entier: 'Je lis d\'abord le dernier entier passé, puis les parts qui restent après lui.',
    regle: 'Je compte les parts dans une unité, ça donne le bas. Je compte les parts depuis 0, ça donne le haut.',
};

/**
 * Candidats pour un point (i, den) selon la forme d'affichage. Chaque
 * candidat porte sa valeur exacte (num/den) pour le contrôle d'égalité.
 * Un candidat au-delà de la fin de la droite (`end`) est écarté : un
 * distracteur hors figure est mort (règle du parc, 623 et 1065).
 */
const candidatsLire = (i, den, forme, end) => {
    const q = Math.floor(i / den);
    const r = i % den;
    const F = (n, d, code) => ({ texte: `$${fracTex(n, d)}$`, code, num: n, den: d });
    const D = (s, code, num, dn) => ({ texte: s, code, num, den: dn });
    const decal = randomChoice([i - 1, i + 1].filter((x) => x >= 1));
    const c = [];
    if (forme === 'fraction') {
        if (den > 2) c.push(F(i, den - 1, 'traits_interieurs'));
        c.push(F(i + 1, den + 1, 'traits_bornes'));
        c.push(F(decal, den, 'decalage'));
        if (i > den) c.push(F(r, den, 'oubli_entier'));
        if (i > den) c.push(F(q * 10 + r, den, 'coller'));
        if (i > den && den !== 10) c.push(D(`${q},${r}`, 'coller', q * 10 + r, 10));
        // inversion seulement si elle s'écrit comme une vraie fraction (jamais « 2/1 »)
        if (i > 1) c.push(F(den, i, 'inversion'));
    } else {
        if (i > den && den !== 10) c.push(D(`${q},${r}`, 'coller', q * 10 + r, 10));
        if (i > den) c.push(D(decStr(r, den), 'oubli_entier', r, den));
        c.push(D(decStr(decal, den), 'decalage', decal, den));
        if (den === 4) c.push(D(decStr(i + 1, 5), 'traits_bornes', i + 1, 5));
    }
    return c.filter((d) => d.num <= end * d.den);
};

/**
 * Groupe d'un candidat selon ce qu'il partage avec la clé i/den (forme
 * fraction) : D = même dénominateur (décalage, entier oublié, coller en
 * fraction), N = même numérateur (traits intérieurs), R = rien (traits
 * bornes, inversion, coller décimal).
 */
const groupe = (d) => {
    if (d.code === 'traits_interieurs') return 'N';
    if (d.code === 'decalage' || d.code === 'oubli_entier' || (d.code === 'coller' && /^\$/.test(d.texte))) return 'D';
    return 'R';
};

/**
 * TROIS RÉGIMES D'APPARIEMENT en rotation (revue prof 2026-09-07), pour
 * qu'aucune stratégie sans lecture ne dépasse la moitié des items :
 *   den_num  (40 %) : un D + un N. Le demi-geste « compter le bas » ne
 *                     sépare pas la clé du D, « compter le haut » ne la
 *                     sépare pas du N ;
 *   den_den  (30 %) : deux D. La clé n'est repérable ni par le bas, ni
 *                     comme « le choix qui ressemble aux deux autres » ;
 *   num_rien (30 %) : un N + un R. Garde l'inversion et le coller décimal
 *                     dans le QCM fraction.
 * Fuites mesurées après (tmp-1050-lireqcm) : bas seul ≈ 30 %, haut seul
 * ≈ 30 %, intersection ≈ 40 %, aucune au-dessus de 50 %. Un régime
 * impossible (moitiés : pas de N ; 1/2 : un seul D) se replie sur le
 * suivant, en dernier recours D + R. Forme décimale : deux familles au
 * hasard (pas de composantes à protéger).
 * null si collision de valeur (avec la clé ou entre eux) ou de texte.
 */
const REGIMES = ['den_num', 'den_num', 'den_num', 'den_num', 'den_den', 'den_den', 'den_den', 'num_rien', 'num_rien', 'num_rien'];

const apparier = (cands, regime) => {
    const D = cands.filter((d) => groupe(d) === 'D');
    const N = cands.filter((d) => groupe(d) === 'N');
    const R = cands.filter((d) => groupe(d) === 'R');
    const deuxDe = (liste) => {
        const codes = [...new Set(liste.map((d) => d.code))];
        if (codes.length < 2) return null;
        const [c1, c2] = shuffle(codes);
        return [randomChoice(liste.filter((d) => d.code === c1)), randomChoice(liste.filter((d) => d.code === c2))];
    };
    const unEtUn = (a, b) => (a.length && b.length ? [randomChoice(a), randomChoice(b)] : null);
    const essais = {
        den_num: () => unEtUn(D, N) || deuxDe(D) || unEtUn(N, R) || unEtUn(D, R),
        den_den: () => deuxDe(D) || unEtUn(D, N) || unEtUn(N, R) || unEtUn(D, R),
        num_rien: () => unEtUn(N, R) || unEtUn(D, N) || deuxDe(D) || unEtUn(D, R),
    };
    return essais[regime]();
};

const distracteursLire = (i, den, forme, end) => {
    const cands = candidatsLire(i, den, forme, end);
    if (cands.length < 2) return null;
    let choix;
    let regime = null;
    const inversion = cands.find((d) => d.code === 'inversion');
    const D = cands.filter((d) => groupe(d) === 'D');
    if (forme === 'fraction' && den === 2 && inversion && D.length) {
        // MOITIÉS (revue prof 2026-09-08) : aucune erreur ne garde le
        // numérateur avec un autre bas (ce serait 3/1) ; l'élève qui a compté
        // « 3 » doit au moins savoir où il va : l'inversion (2/3 face à 3/2)
        // est toujours là, avec un distracteur de même bas.
        regime = 'inversion';
        choix = [randomChoice(D), inversion];
    } else if (forme === 'fraction') {
        regime = randomChoice(REGIMES);
        choix = apparier(cands, regime);
    } else {
        const premier = randomChoice(cands);
        const autres = cands.filter((d) => d.code !== premier.code);
        choix = autres.length ? [premier, randomChoice(autres)] : null;
    }
    if (!choix) return null;
    const [premier, second] = choix;
    if (choix.some((d) => memeValeur(d.num, d.den, i, den))) return null;
    if (memeValeur(premier.num, premier.den, second.num, second.den)) return null;
    if (premier.texte === second.texte) return null;
    choix.regime = regime;
    return choix;
};

// ─── Carte 1 : Série flash (4 droites, saisie deux cases) ─────────────────

// Écriture imposée en toutes lettres (revue prof 2026-09-07) : deux droites
// d'une même série peuvent porter la même valeur (3/2 et 15/10), et 3/2 pour
// 15/10 serait compté faux par la correction positionnelle.
const CONSIGNE_FLASH = '<p><b>Écris l\'abscisse de chaque point avec la fraction de la graduation</b> : le dénominateur est le nombre de parts d\'une unité. <b>Ne simplifie pas.</b></p>';

/**
 * Les quatre réponses sur UNE ligne KaTeX, police réduite d'un cran et
 * séparateurs serrés (retours écran prof 2026-09-08). Une ligne KaTeX ne
 * se replie jamais ; ce qui tient sur un téléphone (≈ 343 px utiles), c'est
 * la SOMME des cases : en ligne KaTeX une case est calée sur un gabarit
 * invisible de 3 caractères (≈ 36 px), qui suit la police, et non sur la
 * largeur fixe des trous en mode texte. Quatre fractions avec leur lettre
 * ≈ 280 px, trois séparateurs « \; ; \; » ≈ 55 px. Les « \quad » d'une
 * première version (≈ 40 px chacun) faisaient déborder la ligne.
 *   « A : ⬚/⬚ ; B : ⬚/⬚ ; C : ⬚/⬚ ; D : ⬚/⬚ »
 * Si un écran étroit déborde encore : repasser à deux par ligne (\n).
 */
const ligneFlash = (items) => `$\\small ${items.map((it) => `\\text{${it.label} : } ${fracDeuxCases(it.i, it.den)}`).join(' \\; ; \\; ')}$`;

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const dens = shuffle(DENS);
        const lettres = ['A', 'B', 'C', 'D'];
        // Points PREMIERS avec le dénominateur seulement (pas de 2/4 ni de
        // 15/10) : les fractions égales attendent l'automatisme 1051, qui
        // reprendra ce dispositif à quatre droites (revue prof 2026-09-08).
        const items = dens.map((den, k) => ({ den, i: tirerRang(den, { simplifiable: false }), label: lettres[k] }));
        // au moins deux points au-delà de 1
        if (items.filter((it) => it.i > it.den).length < 2) continue;
        // jamais deux droites de même valeur sous deux écritures (3/2 et
        // 15/10) : ce sont les fractions égales, l'automatisme 1051, pas 1050
        // (revue prof 2026-09-08)
        const valeurs = items.map((it) => it.i / it.den);
        if (new Set(valeurs.map((v) => Math.round(v * 1e6))).size < 4) continue;
        const droites = items.map((it) => elDroite(configDroite(it.den, [it], { end: finDroite([it.i], it.den) })));
        const texte = CONSIGNE_FLASH + items.map((_, k) => `{{element:${k}}}`).join('');
        const phrase = ligneFlash(items);
        const payload = makeTexteTrou(phrase, texte, {
            ...opts(L_FLASH), mode: 'saisie', strict: true, elements_additionnels: elements(droites),
        });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { items: items.map(({ den, i, label }) => ({ den, i, label })) };
        return payload;
    }
    // Repli déterministe : A 3/2, B 7/3, C 1/4, D 13/10 (deux au-delà de 1, tous premiers, valeurs distinctes).
    const items = [{ den: 2, i: 3, label: 'A' }, { den: 3, i: 7, label: 'B' }, { den: 4, i: 1, label: 'C' }, { den: 10, i: 13, label: 'D' }];
    const droites = items.map((it) => elDroite(configDroite(it.den, [it], { end: finDroite([it.i], it.den) })));
    const texte = CONSIGNE_FLASH + items.map((_, k) => `{{element:${k}}}`).join('');
    const phrase = ligneFlash(items);
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_FLASH), mode: 'saisie', strict: true, elements_additionnels: elements(droites),
    });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2 : Quelle graduation ? (QCM, Saisie) ──────────────────────────

/** Une droite sans point, fin 2 ou 3, étiquettes et origine variables. */
const droiteNue = () => {
    const den = randomChoice(DENS);
    const end = randomChoice([2, 3]);
    const etiquettes = randomChoice(ETIQUETTES);
    const lineType = tirerLineType();
    return { den, end, etiquettes, lineType, config: configDroite(den, [], { end, etiquettes, lineType }) };
};

const genGradQcm = () => {
    const d = droiteNue();
    const total = d.end * d.den + 1;
    const inter = { texte: String(d.den - 1), code: 'traits_interieurs' };
    const bornes = { texte: String(d.den + 1), code: 'traits_bornes' };
    const toutes = { texte: String(total), code: 'toutes_graduations' };
    // Trois jeux en rotation (revue prof 2026-09-07) : traits intérieurs +
    // bornes, traits intérieurs + total, bornes + total. BIAIS DE POSITION
    // RÉSIDUEL, STRUCTUREL : une seule erreur de lecture donne moins que le
    // dénominateur (compter les traits intérieurs), la clé n'est donc jamais
    // la plus grande des trois valeurs (plus petite une fois sur deux, au
    // milieu une fois sur deux). Fabriquer un second distracteur en dessous
    // serait inventer une erreur que personne ne fait. Ne pas rouvrir.
    // Moitiés : « 1 part » ne trompe personne → seul jeu bornes + total, la
    // clé y est toujours la plus petite.
    const faux = d.den === 2 ? [bornes, toutes] : randomChoice([[inter, bornes], [inter, toutes], [bornes, toutes]]);
    const bon = String(d.den);
    const texte = `<p><b>Regarde la ${support(d.lineType)} graduée.</b> En combien de parts égales l'unité est-elle partagée ?</p>{{element:0}}`;
    const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, texte, {
        ...opts(L_GRAD), elements_additionnels: elements([elDroite(d.config)]),
    });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { den: d.den, end: d.end, etiquettes: d.etiquettes, lineType: d.lineType, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

const genGradSaisie = () => {
    const d = droiteNue();
    const texte = `<p><b>Regarde la ${support(d.lineType)} graduée et complète.</b></p>{{element:0}}`;
    const phrase = `L'unité est partagée en {{${d.den}}} parts égales.`;
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_GRAD), mode: 'saisie', strict: true, elements_additionnels: elements([elDroite(d.config)]),
    });
    payload.contenu = { ...payload.contenu, explications: { traits_interieurs: EXPLICATIONS.traits_interieurs, regle: EXPLICATIONS.regle } };
    payload._meta.params = { den: d.den, end: d.end, etiquettes: d.etiquettes, lineType: d.lineType };
    return payload;
};

// ─── Carte 3 : Lire l'abscisse (QCM, Relier, Vrai/Faux) ───────────────────

const genLireQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const den = randomChoice(DENS);
        const finMax = finMaxLire(den);
        const i = tirerRang(den, { simplifiable: randomChoice([true, false, null]), finMax });
        // clé décimale une fois sur trois quand elle existe
        const forme = aDecimal(den) && randomChoice([true, false, false]) ? 'decimal' : 'fraction';
        const end = finDroite([i], den, finMax);
        const faux = distracteursLire(i, den, forme, end);
        if (!faux) continue;
        const bon = forme === 'fraction' ? `$${fracTex(i, den)}$` : decStr(i, den);
        const choix = [bon, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 3) continue;
        const etiquettes = randomChoice(ETIQUETTES);
        const lineType = tirerLineType();
        const config = configDroite(den, [{ i, label: 'A' }], { end, etiquettes, lineType });
        const texte = `<p><b>Quelle est l'abscisse du point A ?</b>${forme === 'decimal' ? ' Réponds en écriture décimale.' : ''}</p>{{element:0}}`;
        const payload = makeQcm(choix, bon, texte, { ...opts(L_LIRE), elements_additionnels: elements([elDroite(config)]) });
        payload.contenu = { ...payload.contenu, sousType: forme, explications: EXPLICATIONS };
        payload._meta.params = { den, i, forme, etiquettes, lineType, regime: faux.regime, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : 7/4, traits_interieurs 7/3, décalage 6/4.
    const config = configDroite(4, [{ i: 7, label: 'A' }], { end: 3 });
    const bon = `$${fracTex(7, 4)}$`;
    const payload = makeQcm([bon, `$${fracTex(7, 3)}$`, `$${fracTex(6, 4)}$`], bon,
        '<p><b>Quelle est l\'abscisse du point A ?</b></p>{{element:0}}', { ...opts(L_LIRE), elements_additionnels: elements([elDroite(config)]) });
    payload.contenu = { ...payload.contenu, sousType: 'fraction', explications: EXPLICATIONS };
    payload._meta.params = { den: 4, i: 7, forme: 'fraction', etiquettes: 'tous', lineType: 'half', distracteurs: [{ code: 'traits_interieurs' }, { code: 'decalage' }] };
    return payload;
};

/** Rangs distincts, au moins un au-delà de 1, lettres mêlées sur les positions. */
const tirerPointsRelier = (den, nb, finMax = END_MAX) => {
    const rangs = [tirerRang(den, { auDela: true, finMax })];
    for (let essai = 0; essai < 40 && rangs.length < nb; essai++) {
        const i = tirerRang(den, { exclus: rangs, finMax });
        if (!rangs.includes(i)) rangs.push(i);
    }
    if (rangs.length < nb) return null;
    rangs.sort((a, b) => a - b);
    const lettres = shuffle(['A', 'B', 'C', 'D'].slice(0, nb));
    return rangs.map((i, k) => ({ i, label: lettres[k] }));
};

/** Moitiés exclues du Relier : trois rangs valides seulement (1/2, 3/2, 5/2), il en faut quatre. */
const DENS_RELIER = DENS.filter((den) => den !== 2);

const genRelier = () => {
    // Le dénominateur est tiré UNE fois, les retirages se font dedans (revue
    // prof 2026-09-08) : sinon les tiers, plus contraints, échouaient et
    // cédaient la place aux dixièmes (73 % des tirages).
    const den = randomChoice(DENS_RELIER);
    const finMax = finMaxLire(den);
    for (let essai = 0; essai < 80; essai++) {
        const points = tirerPointsRelier(den, 4, finMax);
        if (!points) continue;
        const rangs = points.map((p) => p.i);
        // leurre 1 : décalage d'un sur un point ; leurre 2 : entier oublié sur un point > 1
        const cible = randomChoice(points);
        const decal = randomChoice([cible.i - 1, cible.i + 1]);
        const auDela = points.filter((p) => p.i > den);
        const oubli = randomChoice(auDela).i % den;
        const leurres = [{ n: decal, code: 'decalage' }, { n: oubli, code: 'oubli_entier' }];
        if (leurres.some((l) => l.n < 1 || l.n % den === 0 || rangs.includes(l.n))) continue;
        if (leurres[0].n === leurres[1].n) continue;
        const lineType = tirerLineType();
        const etiquettes = randomChoice(ETIQUETTES);
        const config = configDroite(den, points, { end: finDroite(rangs, den, finMax), etiquettes, lineType });
        const pairs = points.map((p) => ({ source: p.label, target: `$${fracTex(p.i, den)}$` }));
        const ciblesLeurres = leurres.map((l) => ({ target: `$${fracTex(l.n, den)}$`, code: l.code }));
        const texte = `<p><b>Relie chaque point à son abscisse.</b> Deux abscisses ne correspondent à aucun point.</p>{{element:0}}`;
        const payload = makeRelier(pairs, texte, {
            ...opts(L_LIRE), cibles_leurres: ciblesLeurres, elements_additionnels: elements([elDroite(config)]),
        });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { den, points, etiquettes, lineType, distracteurs: leurres.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : quarts, A 1/4, C 3/4, B 6/4, D 9/4 ; leurres 5/4 (décalage de 6/4), 2/4 (entier oublié de 6/4).
    const points = [{ i: 1, label: 'A' }, { i: 3, label: 'C' }, { i: 6, label: 'B' }, { i: 9, label: 'D' }];
    const config = configDroite(4, points, { end: 3 });
    const pairs = points.map((p) => ({ source: p.label, target: `$${fracTex(p.i, 4)}$` }));
    const payload = makeRelier(pairs,
        '<p><b>Relie chaque point à son abscisse.</b> Deux abscisses ne correspondent à aucun point.</p>{{element:0}}', {
            ...opts(L_LIRE),
            cibles_leurres: [{ target: `$${fracTex(5, 4)}$`, code: 'decalage' }, { target: `$${fracTex(2, 4)}$`, code: 'oubli_entier' }],
            elements_additionnels: elements([elDroite(config)]),
        });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { den: 4, points, etiquettes: 'tous', lineType: 'half', distracteurs: [{ code: 'decalage' }, { code: 'oubli_entier' }] };
    return payload;
};

/**
 * Une affirmation « Le point A a pour abscisse … » sous une forme (fraction,
 * mixte, décimal), vraie ou fausse ; la fausse porte un code de famille.
 */
const affirmation = (p, den, forme, vraie, end) => {
    const debut = `Le point ${p.label} a pour abscisse `;
    const q = Math.floor(p.i / den);
    const r = p.i % den;
    if (vraie) {
        if (forme === 'fraction') return { ligne: `${debut}${tex(fracTex(p.i, den))}`, code: null };
        if (forme === 'mixte') return { ligne: `${debut}${tex(mixteTex(p.i, den))}`, code: null };
        return { ligne: `${debut}${decStr(p.i, den)}`, code: null };
    }
    if (forme === 'mixte') {
        // décalage d'une part dans le reste, ou mauvais entier
        const cands = [];
        if (r - 1 >= 1) cands.push({ n: q * den + r - 1, code: 'decalage' });
        if (r + 1 <= den - 1) cands.push({ n: q * den + r + 1, code: 'decalage' });
        cands.push({ n: (q + 1) * den + r, code: 'decalage_entier' });
        if (q >= 2) cands.push({ n: (q - 1) * den + r, code: 'decalage_entier' });
        const surLaDroite = cands.filter((c) => c.n <= end * den);
        if (!surLaDroite.length) return null;
        const c = randomChoice(surLaDroite);
        return { ligne: `${debut}${tex(mixteTex(c.n, den))}`, code: c.code };
    }
    const faux = candidatsLire(p.i, den, forme, end).filter((d) => !memeValeur(d.num, d.den, p.i, den));
    if (!faux.length) return null;
    // « coller » (1,3 pour 1 + 3/4) est le faux préféré quand il existe
    const coller = faux.filter((d) => d.code === 'coller' && /,/.test(d.texte));
    const d = coller.length && randomChoice([true, false]) ? randomChoice(coller) : randomChoice(faux);
    const texteFaux = forme === 'fraction' && /^\$/.test(d.texte) ? tex(d.texte.slice(1, -1)) : d.texte;
    return { ligne: `${debut}${texteFaux}`, code: d.code };
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const den = randomChoice(DENS);
        const points = tirerPointsRelier(den, 3);
        if (!points) continue;
        const formes = points.map((p) => {
            const possibles = ['fraction'];
            if (p.i > den) possibles.push('mixte');
            if (aDecimal(den)) possibles.push('decimal');
            return randomChoice(possibles);
        });
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const end = finDroite(points.map((p) => p.i), den);
        const codes = [];
        const lignes = [];
        let ok = true;
        points.forEach((p, k) => {
            const aff = affirmation(p, den, formes[k], verites[k], end);
            if (!aff) { ok = false; return; }
            if (aff.code) codes.push(aff.code);
            lignes.push(aff.ligne);
        });
        if (!ok || new Set(lignes).size < 3) continue;
        const lineType = tirerLineType();
        const etiquettes = randomChoice(ETIQUETTES);
        const config = configDroite(den, points, { end, etiquettes, lineType });
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>{{element:0}}', {
            ...opts(L_LIRE), elements_additionnels: elements([elDroite(config)]),
        });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { den, points, formes, verites, etiquettes, lineType, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    // Repli déterministe : quarts, A 3/4 (vrai, fraction), B 7/4 « 1,3 » (faux, coller), C 9/4 = 2 + 1/4 (vrai, mixte).
    const points = [{ i: 3, label: 'A' }, { i: 7, label: 'B' }, { i: 9, label: 'C' }];
    const config = configDroite(4, points, { end: 3 });
    const lignes = [
        `Le point A a pour abscisse ${tex(fracTex(3, 4))}`,
        'Le point B a pour abscisse 1,3',
        `Le point C a pour abscisse ${tex(mixteTex(9, 4))}`,
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 0], '<p><b>Vrai ou faux ?</b></p>{{element:0}}', {
        ...opts(L_LIRE), elements_additionnels: elements([elDroite(config)]),
    });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { den: 4, points, formes: ['fraction', 'decimal', 'mixte'], verites: [true, false, true], etiquettes: 'tous', lineType: 'half', distracteurs: [{ code: 'coller' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'trois choix ; chaque distracteur est vérifié différent de la clé EN VALEUR (produit en croix) et des autres ; collision → nouveau tirage complet',
        'dénominateurs 2, 3, 4, 10 ; point jamais sur un entier, jamais 5/3 (sacrifié) ; abscisse ≤ 3',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    grad_qcm_unique: {
        support: '« en combien de parts égales l\'unité est-elle partagée ? », droite sans point, étiquettes tous / 0 et 1 / 0 et 2',
        fixes: { traits_interieurs: 'den − 1, il compte les traits entre 0 et 1 (l\'erreur principale) ; en moitiés remplacé par traits_bornes' },
        rotation: { traits_bornes: 'den + 1, tous les traits bornes comprises', toutes_graduations: 'le nombre de traits de toute la droite' },
    },
    lire_qcm_unique: {
        support: 'un point A, clé en fraction de la graduation, ou décimale une fois sur trois (moitiés, quarts, dixièmes)',
        rotation: {
            traits_interieurs: 'i/(den − 1), deux fois sur trois en premier distracteur',
            traits_bornes: '(i + 1)/(den + 1)',
            decalage: '(i ± 1)/den',
            oubli_entier: '(i mod den)/den, seulement au-delà de 1',
            coller: '13/4 ou 1,3 pour 1 + 3/4 ; 1,3 exclu en dixièmes (il serait juste)',
            inversion: 'den/i',
        },
    },
    lire_relier: '4 points A-D mêlés sur une droite, 4 cibles justes + 2 leurres : décalage d\'un, entier oublié ; 6 valeurs distinctes',
    lire_tableau_vf: '3 points, 3 affirmations aux formes mêlées (fraction, mixte, décimal), 1 ou 2 vraies ; « 1,3 » en faux préféré',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    grad_qcm_unique: genGradQcm,
    grad_texte_trou_saisie: genGradSaisie,
    lire_qcm_unique: genLireQcm,
    lire_relier: genRelier,
    lire_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'grad', label: L_GRAD, formatsList: [
        { key: 'grad_qcm_unique', label: 'QCM' },
        { key: 'grad_texte_trou_saisie', label: 'Saisie trous' },
    ] },
    { id: 'lire', label: L_LIRE, formatsList: [
        { key: 'lire_qcm_unique', label: 'QCM' },
        { key: 'lire_relier', label: 'Relier' },
        { key: 'lire_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
